import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import useOrderService from '../../hooks/useOrderService';
import useBuildingService from '../../hooks/useBuildingService';
import useInvoiceService from '../../hooks/useInvoiceService';
import useDocumentService from '../../hooks/useDocumentService';
import { useSnackbar } from '../../components/SnackbarProvider';
import {
    Typography,
    Paper,
    Grid,
    Table,
    TableContainer,
    TableHead,
    TableBody,
    Autocomplete,
    TableRow,
    TableCell,
    CircularProgress,
    Button,
    Dialog,
    DialogActions,
    FormHelperText,
    DialogContent,
    Box,
    DialogTitle,
    TextField,
    Checkbox
} from '@mui/material';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import Accordion from '../../components/Accordion';
import InvoiceRetentionTable from '../../components/InvoiceRetentionTable';
import UploadDocument from '../../components/UploadDocument';
import { formatDate, formatNumber, formatPrice, DatePickerComponent, handleApiError, itemsPerPage, IconBtn } from '../../components/Utils';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';

dayjs.extend(utc);

const EditOrder = () => {
    const { id } = useParams();
    const { getOrder, createDeliveryNoteFromOrder, updateOrder, addBuildingToOrder, disableOrder, disableDeliveryNoteFromOrder } = useOrderService();
    const { getBuildings } = useBuildingService();
    const { createInvoice, disableInvoice } = useInvoiceService();
    const { getDocumentBlob } = useDocumentService();
    const showSnackbar = useSnackbar();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [order, setOrder] = useState(null);
    const [openInvoice, setOpenInvoice] = useState(false);
    const [quantities, setQuantities] = useState({});
    const [quantityErrors, setQuantityErrors] = useState({});
    const [inputErrors, setInputErrors] = useState({});

    const [selectedDNs, setSelectedDNs] = useState([]);

    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [deleteId, setDeleteId] = useState(false);
    const [typeDelete, setTypeDelete] = useState(false);

    const [buildings, setBuildings] = useState([]);
    const [selectedBuilding, setSelectedBuilding] = useState(null);

    const [invoices, setInvoices] = useState([]);
    const [totalPriceSum, setTotalPriceSum] = useState(null);
    const [paidAmountSum, setPaidAmountSum] = useState(null);
    const [remainingAmountSum, setRemainingAmountSum] = useState(null);
    const [totalRetention, setTotalRetention] = useState(null);
    const [paidRetention, setPaidRetention] = useState(null);
    const [remainingRetentionSum, setRemainingRetentionSum] = useState(null);
    const [orderProviderCode, setOrderProviderCode] = useState(null);

    const [deliveryNotes, setDeliveryNotes] = useState([]);
    const [deliveryNotesPage, setDeliveryNotesPage] = useState(1);

    const [retention, setRetention] = useState(null);
    const [retentionDate, setRetentionDate] = useState(null);
    const [invoiceLimitDate, setInvoiceLimitDate] = useState(null);
    const [vat, setVat] = useState(null);
    const [externalProviderId, setExternalProviderId] = useState(null);
    const [invoiceDate, setInvoiceDate] = useState(dayjs().startOf('day').local().format());

    const [errors, setErrors] = useState({
        vat: '',
        retention: '',
        external_provider_id: '',
    });

    const fetchOrder = useCallback(async () => {
        try {
            setLoading(true);
            const response = await getOrder(id);
            const order = response.data.order
            setOrder(order);
            setOrderProviderCode(order.provider_code);
            setDeliveryNotes(response.data.delivery_notes);
            setInvoices(response.data.invoices);
            setTotalPriceSum(response.data.total_price_sum);
            setPaidAmountSum(response.data.paid_amount_sum);
            setRemainingAmountSum(response.data.remaining_amount_sum);
            setTotalRetention(response.data.total_retentions);
            setPaidRetention(response.data.paid_retention);
            setRemainingRetentionSum(response.data.remaining_retention_sum);
            setLoading(false);
        } catch (error) {
            setError(error.message);
            setLoading(false);
        }
    }, [id]);

    const fetchBuildings = useCallback(async () => {
        try {
            setLoading(true);
            const response = await getBuildings();
            setBuildings(response.data.items);
            setLoading(false);
        } catch (error) {
            setError(error.message);
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchOrder();
        fetchBuildings();
    }, [id]);

    useEffect(() => {
        const total = deliveryNotes.filter(item => !item.disabled).length;
        const totalPages = Math.max(1, Math.ceil(total / itemsPerPage));
        if (deliveryNotesPage > totalPages) {
            setDeliveryNotesPage(totalPages);
        }
    }, [deliveryNotes, deliveryNotesPage]);

    const handleClickOpenInvoice = () => {
        setSelectedDNs([]);
        setOpenInvoice(true);
    };

    const handleCloseInvoice = () => {
        setRetention(null);
        setRetentionDate(null);
        setVat(null);
        setExternalProviderId(null);
        setInvoiceLimitDate(null);
        setOpenInvoice(false);
    };
    const handleChange = (event) => {
        const { name, value } = event.target;
        if (name === 'retention') {
            setRetention(value)
        } else if (name === 'vat') {
            setVat(value)
        } else if (name === 'external_provider_id') {
            if (value.length > 150) {
                setErrors(prevErrors => ({ ...prevErrors, [name]: 'El valor no debe exceder los 50 caracteres' }));
            } else {
                setErrors(prevErrors => ({ ...prevErrors, [name]: '' }));

            }
            setExternalProviderId(value)
            return
        }
        const numericValue = Number(value);
        // Verificar que el valor es un número entero y está en el rango permitido
        if (!isNaN(numericValue) && Number.isInteger(numericValue) && numericValue >= 0 && numericValue <= 100) {
            setErrors(prevErrors => ({ ...prevErrors, [name]: '' }));
        } else {
            setErrors(prevErrors => ({ ...prevErrors, [name]: 'El valor debe ser un número entero entre 0 y 100' }));
        }
    };

    const handleDateChange = (name, newValue) => {
        const date = dayjs(newValue).hour(12).minute(0).second(0).utc().format('YYYY-MM-DD');
        if (name === 'retention_limit_date') {
            setRetentionDate(date);
        } else if (name === 'invoice_date') {
            setInvoiceDate(invoiceDate);
        } else if (name === 'invoice_limit_date') {
            setInvoiceLimitDate(date);
        }
    };
    const handleCreate = async () => {
        const delNoteData = {
            order_id: id,
            products: Object.entries(quantities).map(([productId, quantity]) => ({
                product_id: productId,
                quantity: parseFloat(quantity)
            }))
        };
        try {
            const result = await createDeliveryNoteFromOrder(delNoteData);
            if (result.status < 300) {
                showSnackbar('Albarán creado correctamente', 'success');
                fetchOrder();
                setQuantities({});
                setError(null);
            } else {
                setError(`Error ${result.status}: ${result.data.detail}`);
            }
        } catch (error) {
            setError(error.message);
        }
    };

    const handleCreateInvoice = async () => {
        try {
            const result = await createInvoice({
                    delivery_notes: selectedDNs,
                    retention: retention,
                    vat: vat,
                    invoice_limit_date: invoiceLimitDate,
                    external_provider_id: externalProviderId,
                    invoice_date: invoiceDate,
                    retention_limit_date: retentionDate
                });
            if (result.status < 300) {
                showSnackbar('Factura creada correctamente', 'success');
                fetchOrder();
                handleCloseInvoice(false);
                setError(null);
            } else {
                setError(`Error ${result.status}: ${result.data.detail}`);
            }
        } catch (error) {
            setError(error.message);
        }
    };


    const handleUpdateOrder = async () => {
        try {
            const result = await updateOrder({
                    order_id: id,
                    provider_code: orderProviderCode
                });
            if (result.status < 300) {
                showSnackbar('Pedido actualizado correctamente', 'success');
                fetchOrder();
                setError(null);
            } else {
                setError(`Error ${result.status}: ${result.data.detail}`);
            }
        } catch (error) {
            setError(error.message);
        }
    };

    const handleQuantityChange = (id, value) => {
        const quantity = parseFloat(value);
        const orderedQuantity = order.product_orders.find(item => item.id === id).quantity;
        const receivedQuantity = order.product_orders.find(item => item.id === id).received_quantity || 0;

        let error = false;
        let inputError = false; // New input error flag

        if (isNaN(quantity) || quantity < 0 || quantity > orderedQuantity) {
            error = true;
            inputError = isNaN(quantity); // Set input error if value is not a number
        } else if (receivedQuantity + quantity > orderedQuantity) {
            error = true;
        }

        setQuantityErrors(prevErrors => ({
            ...prevErrors,
            [id]: error
        }));

        setInputErrors(prevErrors => ({
            ...prevErrors,
            [id]: inputError
        }));

        setQuantities(prevQuantities => ({
            ...prevQuantities,
            [id]: quantity
        }));
    };

    const handleDNChange = (id) => {
        setSelectedDNs(prevSelected =>
            prevSelected.includes(id)
                ? prevSelected.filter(albaranId => albaranId !== id)
                : [...prevSelected, id]
        );
    };

    if (loading) {
        return <CircularProgress />;
    }

    if (!order) {
        return <Typography variant="body1">Pedido no encontrado</Typography>;
    }


    const handleBuildingChange = (event, newValue) => {
        setSelectedBuilding(newValue);
    };


    const changeBuilding = async () => {
        try {
            setLoading(true);
            const result = await addBuildingToOrder({ order_id: id, building_id: selectedBuilding.id });
            if (result.status < 300) {
                showSnackbar('Obra añadida correctamente', 'success');
                fetchOrder();
            } else {
                showSnackbar(`Error ${result.status}: ${result.data.detail}`, 'error');
            }
        } catch (error) {
            showSnackbar(handleApiError(error, 'Error al cambiar obra.'), 'error');
        } finally {
            setLoading(false);
        }
    };
    const handleOpenDeleteDialog = (itemId, type) => {
        setDeleteId(itemId);
        setTypeDelete(type);
        setOpenDeleteDialog(true);
    };
    const handleCloseDeleteDialog = () => {
        setOpenDeleteDialog(false);
    };
    const handleDeleteItem = async () => {
        try {
            let result;
            if (typeDelete === '/orders/disable_dn') {
                result = await disableDeliveryNoteFromOrder(deleteId);
            } else if (typeDelete === '/orders/disable') {
                result = await disableOrder(deleteId);
            } else if (typeDelete === '/invoices/disable') {
                result = await disableInvoice(deleteId);
            }

            if (result.status < 300) {
                showSnackbar('Eliminado correctamente', 'success');
                if (typeDelete === '/orders/disable') {
                    navigate(`/orders/list`);
                }
                fetchOrder();
                setOpenDeleteDialog(false);
            } else {
                showSnackbar(`Error ${result.status}: ${result.data.detail}`, 'error');
            }
        } catch (error) {
            showSnackbar(handleApiError(error, 'Error al eliminar.'), 'error');
        };
    }

    const handleDownloadDocument = async (documentId, documentName) => {
        try {
            const response = await getDocumentBlob(documentId);
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', documentName);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            setError(handleApiError(error, 'Error al descargar el documento.'));
        }
    };

    const hasErrors = Object.values(quantityErrors).some(error => error);
    const allQuantitiesFilled = Object.values(quantities).some(qty => qty !== 0 && qty !== '');
    const hasInputErrors = Object.values(inputErrors).some(error => error);
    const hasUninvoicedDNs = deliveryNotes.some(dn => !dn.invoiced);

    // Delivery notes list is fetched embedded in the order (no dedicated paginated
    // endpoint scoped to a single order's display), so pagination is client-side.
    const visibleDeliveryNotes = deliveryNotes.filter(item => !item.disabled);
    const deliveryNotesTotal = visibleDeliveryNotes.length;
    const paginatedDeliveryNotes = visibleDeliveryNotes.slice(
        (deliveryNotesPage - 1) * itemsPerPage,
        deliveryNotesPage * itemsPerPage
    );

    return (
        <div style={{ padding: 20 }}>
            <Grid container spacing={3} style={{ position: 'fixed', top: 60, right: 10, zIndex: 1100, width: 'auto' }}>
                <Grid item>
                    <Paper elevation={0} style={{ padding: '10px', textAlign: 'center', backgroundColor: '#fff' }}>
                        <Box mb={2}>
                            <Typography variant="h5" component="h1" gutterBottom>
                                {order.code}
                            </Typography>
                        </Box>
                    </Paper>
                </Grid>
            </Grid>
            <Accordion title={`Detalles del pedido ${order.code} `}>
                <Grid container spacing={3} style={{ marginBottom: 20 }}>
                    <Grid item xs={3}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>FECHA DEL PEDIDO:</Typography>
                        <Typography variant="body1">
                            {formatDate(order.order_date) || '-'}
                        </Typography>
                    </Grid>
                    <Grid item xs={3}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>ESTADO DEL PEDIDO:</Typography>
                        <Typography variant="body1">
                            {order.order_status === 'partly_received' ? 'PARCIALMENTE ACEPTADO' :
                                order.order_status === 'received' ? 'RECIBIDO' :
                                    order.order_status === 'pending' ? 'PENDIENTE' :
                                        order.order_status || '-'}
                        </Typography>
                    </Grid>
                    <Grid item xs={3}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>IMPORTE TOTAL:</Typography>
                        <Typography variant="body1" >
                            {formatPrice(order.total_price)}
                        </Typography>
                    </Grid>
                    <Grid item xs={3} >
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>BORRAR EL PEDIDO:</Typography>
                        <Button
                            variant="contained"
                            color="secondary"
                            onClick={() => handleOpenDeleteDialog(order.id, '/orders/disable')}
                        >
                            Borrar
                        </Button>
                    </Grid>
                    <Grid item xs={3}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>PROVEEDOR:</Typography>
                        <Typography variant="body1">
                            <Link to={`/providers/update/${order.provider.id}`} >
                                {order.provider.code || '-'}
                            </Link>
                        </Typography>
                        <Typography variant="body1">{order.provider.name || '-'}</Typography>
                        <Typography variant="body1">{order.provider.telephone_number || '-'}</Typography>
                    </Grid>
                    <Grid item xs={3}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>CÓDIGO EXTERNO PROVEEDOR:</Typography>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={9}>
                                <TextField
                                    fullWidth
                                    label="Código proveedor"
                                    name="provider_code"
                                    value={orderProviderCode}
                                    onChange={(newValue) => setOrderProviderCode(newValue.target.value)}
                                    margin="normal"
                                    inputProps={{ maxLength: 36 }}
                                />
                            </Grid>
                            <Grid item xs={3} md={2}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleUpdateOrder}
                                    sx={{ mt: 2 }}
                                >
                                    Cambiar
                                </Button>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item xs={3}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>OBRA:</Typography>
                        {order.building && order.building.code && order.building.name ? (
                            <>
                                <Typography variant="body1">
                                    <Link to={`/buildings/update/${order.building.id}`}>
                                        {order.building.code || '-'}
                                    </Link>
                                </Typography>
                                <Typography variant="body1">{order.building.name || '-'}</Typography>
                            </>
                        ) : (
                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs={9} md={5} mt={2}>
                                    <Autocomplete
                                        options={buildings}
                                        getOptionLabel={(option) => option.code?.toString() + ' - ' + option.name?.toString()}
                                        value={selectedBuilding}
                                        onChange={handleBuildingChange}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Seleccionar obra"
                                                fullWidth
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={3} md={2}>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={changeBuilding}
                                        fullWidth
                                    >
                                        Cambiar
                                    </Button>
                                </Grid>
                            </Grid>
                        )}
                    </Grid>

                </Grid>


                <Typography variant="h5" gutterBottom>
                    Productos del pedido
                </Typography>
                <Button variant="contained" color="primary" disabled={hasErrors || !allQuantitiesFilled || hasInputErrors} onClick={handleCreate} style={{ marginTop: 20 }}>
                    Crear albarán
                </Button>
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Nombre del Producto</TableCell>
                                <TableCell>Importe</TableCell>
                                <TableCell>Precio individual</TableCell>
                                <TableCell>Cantidad pedida</TableCell>
                                <TableCell>Cantidad recibida</TableCell>
                                <TableCell>Cantidad a recibir</TableCell>
                                <TableCell>Estado</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {order.product_orders.map((item) => (
                                <TableRow key={item.id}>
                                    <TableCell>{item.product_name}</TableCell>
                                    <TableCell>
                                        {item.price && item.quantity ? (item.price * item.quantity).toLocaleString('es-ES', { useGrouping: true, minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €' : '0,00€'}
                                    </TableCell>
                                    <TableCell>
                                        {formatPrice(item.price)}
                                    </TableCell>
                                    <TableCell>{formatNumber(item.quantity)} {item.unit === 'l' ? 'LITROS' :
                                        item.unit === 'kg' ? 'KGS' :
                                            item.unit === 'uds' ? 'UNIDADES' :
                                                item.unit}
                                    </TableCell>
                                    <TableCell>{formatNumber(item.received_quantity)} {item.unit === 'l' ? 'LITROS' :
                                        item.unit === 'kg' ? 'KGS' :
                                            item.unit === 'uds' ? 'UNIDADES' :
                                                item.unit}
                                    </TableCell>
                                    <TableCell>
                                        <TextField
                                            type="number"
                                            value={quantities[item.id] || ''}
                                            onChange={(e) => handleQuantityChange(item.id, e.target.value)}
                                            inputProps={{ min: 0, max: item.quantity }}
                                            fullWidth
                                            disabled={item.received_quantity === item.quantity}
                                            error={quantityErrors[item.id] || inputErrors[item.id] || false} // Add input error check
                                        />
                                        {(quantityErrors[item.id] || inputErrors[item.id]) && (
                                            <FormHelperText error>
                                                Valor no válido
                                            </FormHelperText>
                                        )}

                                    </TableCell>
                                    <TableCell>
                                        {item.product_order_status === 'received' ? 'RECIBIDO' :
                                            item.product_order_status === 'pending' ? 'PENDIENTE' :
                                                item.product_order_status === 'partly_received' ? 'PARCIALMENTE RECIBIDO' :
                                                    item.product_order_status || '-'}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                {error && (
                    <Typography variant="body2" color="error" style={{ marginTop: 10 }}>
                        {error}
                    </Typography>
                )}

            </Accordion>
            <Accordion
                title='Albaranes'
                paginate
                totalItems={deliveryNotesTotal}
                currentPage={deliveryNotesPage}
                onPageChange={setDeliveryNotesPage}
            >
                <Button variant="contained" color="primary" style={{ marginTop: 20 }} onClick={handleClickOpenInvoice} disabled={!hasUninvoicedDNs}>
                    Crear factura
                </Button>
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Código</TableCell>
                                <TableCell>Facturado</TableCell>
                                <TableCell>Fecha</TableCell>
                                <TableCell>Acciones</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginatedDeliveryNotes.map(item => (
                                <TableRow key={item.id}>
                                    <TableCell>
                                        <Link to={`/delivery_notes/update/${item.id}`}>
                                            {item.code || '-'}
                                        </Link>
                                    </TableCell>
                                    <TableCell>{item.invoiced ? "Sí" : "No"}</TableCell>
                                    <TableCell>{formatDate(item.dn_date)}</TableCell>
                                    <TableCell>
                                        {item.documents && item.documents.length > 0 && (
                                            <IconBtn
                                                icon={<DownloadIcon />}
                                                title={item.documents[0].name}
                                                onClick={() =>
                                                    handleDownloadDocument(item.documents[0].id, item.documents[0].name)
                                                }
                                            />
                                        )}
                                        <UploadDocument
                                            to={'delivery_note_id'}
                                            id={item.id}
                                            fetch={fetchOrder}
                                            showDisableDoc={item.documents && item.documents.length}
                                        >
                                        </UploadDocument>
                                        <IconBtn
                                            icon={<DeleteIcon />}
                                            title="Eliminar"
                                            color="error"
                                            onClick={() => handleOpenDeleteDialog(item.id, '/orders/disable_dn')}
                                        />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Accordion>

            <InvoiceRetentionTable
                isInvoice={true}
                isClient={false}
                showBuilding={false}
                showClientOrProvider={false}
                title='Facturas'
                totalAmount={totalPriceSum}
                paidAmount={paidAmountSum}
                remainingAmountSum={remainingAmountSum}
                invoices={invoices}
                uploadDocument={true}
                fetch={fetchOrder}
                handleOpenDeleteDialog={handleOpenDeleteDialog}
            />
            <InvoiceRetentionTable
                isInvoice={false}
                isClient={false}
                showBuilding={false}
                showClientOrProvider={false}
                title='Retenciones'
                totalAmount={totalRetention}
                paidAmount={paidRetention}
                remainingAmountSum={remainingRetentionSum}
                invoices={invoices}
                fetch={fetchOrder}
                handleOpenDeleteDialog={handleOpenDeleteDialog}
            />

            <Dialog open={openInvoice} onClose={handleCloseInvoice}>
                <DialogTitle>Crear Factura</DialogTitle>
                <DialogContent>
                    <br />
                    <Grid>
                        <DatePickerComponent
                            label="Fecha factura"
                            value={invoiceDate}
                            onChange={(newValue) => handleDateChange('invoice_date', newValue)}
                        />
                    </Grid>
                    <br />
                    <Grid>
                        <DatePickerComponent
                            label="Fecha vencimiento factura"
                            value={invoiceLimitDate}
                            onChange={(newValue) => handleDateChange('invoice_limit_date', newValue)}
                        />
                    </Grid>
                    <Grid >
                        <TextField
                            name="vat"
                            label="IVA (%)"
                            fullWidth
                            value={vat}
                            onChange={handleChange}
                            margin="normal"
                            type="number"
                            error={!!errors.vat}
                            helperText={errors.vat}
                        />
                    </Grid>
                    <Grid>
                        <TextField
                            name="retention"
                            label="Retención (porcentaje)"
                            fullWidth
                            value={retention}
                            onChange={handleChange}
                            margin="normal"
                            type="number"
                            error={!!errors.retention}
                            helperText={errors.retention} />
                    </Grid>
                    <Grid>
                        <DatePickerComponent
                            label="Fecha vencimiento retención"
                            value={retentionDate}
                            onChange={(newValue) => handleDateChange('retention_limit_date', newValue)}
                        />
                    </Grid>
                    <Grid>
                        <TextField
                            fullWidth
                            label="Id externo"
                            name="external_provider_id"
                            value={externalProviderId}
                            onChange={handleChange}
                            margin="normal"
                            inputProps={{ maxLength: 50 }}
                            error={!!errors.external_provider_id}
                            helperText={errors.external_provider_id}
                        />
                    </Grid>
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Seleccionar</TableCell>
                                    <TableCell>Código</TableCell>
                                    <TableCell>Fecha</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {deliveryNotes.filter(item => !item.invoiced).map((item) => (
                                    <TableRow key={item.id}>
                                        <TableCell>
                                            <Checkbox
                                                checked={selectedDNs.includes(item.id)}
                                                onChange={() => handleDNChange(item.id)}
                                            />
                                        </TableCell>
                                        <TableCell>{item.code}</TableCell>
                                        <TableCell>{item.dn_date}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseInvoice} color="secondary">
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleCreateInvoice}
                        disabled={
                            !!errors.vat ||
                            !!errors.retention ||
                            !!errors.external_provider_id ||
                            !retention ||
                            !retentionDate ||
                            !vat ||
                            selectedDNs.length <= 0}
                        color="primary">
                        Crear factura
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
                <DialogTitle>¿Estás seguro?</DialogTitle>
                <DialogActions>
                    <Button onClick={handleCloseDeleteDialog} color="primary">
                        Volver
                    </Button>
                    <Button onClick={handleDeleteItem} color="error">
                        Eliminar
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default EditOrder;
